import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/lib/store/auth-store';
import { useQueryClient } from '@tanstack/react-query';
import { RouterLink } from '@/components/router/router-link';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { authControllerSignin, authControllerGoogleSignin, authControllerGetOnboardingStatus } from '@/apis/backend/generated/auth/auth';
import { GoogleSignInButton } from '@/components/auth/google-sign-in-button';
import { Divider } from '@mui/material';

import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Link,
  Stack,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from '@mui/material';

import { paths } from 'src/routes/paths';

export const Route = createFileRoute('/auth/sign-in')({
  validateSearch: (search: Record<string, unknown>) => ({
    shopify_state: search.shopify_state as string | undefined,
    already_connected: search.already_connected as string | undefined,
    error: search.error as string | undefined,
    redirect: search.redirect as string | undefined,
  }),
  component: SignInPage,
});

function SignInPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    shopify_state: shopifyState,
    already_connected: alreadyConnected,
    error: urlError,
    redirect: redirectTo,
  } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Show error from URL (e.g. Shopify install failure)
  useEffect(() => {
    if (urlError) {
      toast.error(urlError);
    }
  }, [urlError]);

  // Shared post-token flow for both email/password and Google sign-in.
  const completeLogin = async (accessToken: string, refreshToken: string) => {
    // Store tokens and fetch user
    await useAuthStore.getState().setTokensAndFetchUser(accessToken, refreshToken);

    toast.success(t('signIn.success'));

    // Store Shopify state for post-auth provisioning
    if (shopifyState) {
      localStorage.setItem('pendingShopifyState', shopifyState);
    }

    // Invalidate organizations cache so fresh data is fetched
    await queryClient.invalidateQueries({ queryKey: ['/organizations'] });

    if (shopifyState) {
      // Redirect to onboarding to trigger Shopify provisioning
      navigate({ to: paths.auth.onboarding });
    } else if (redirectTo) {
      // redirectTo may include a query string (e.g. /figma-auth?code=...) or a
      // fragment (an email footer deep-linking to one card on a settings page),
      // neither of which TanStack's navigate({ to }) parses out of the string —
      // fall back to a real navigation in that case.
      if (/[?#]/.test(redirectTo)) {
        window.location.assign(redirectTo);
      } else {
        navigate({ to: redirectTo });
      }
    } else {
      // Check if onboarding is incomplete before going to dashboard
      try {
        const onboardingStatus = await authControllerGetOnboardingStatus();
        if (onboardingStatus.status !== 'completed') {
          navigate({ to: paths.auth.onboarding });
          return;
        }
      } catch {
        // If status check fails, fall through to dashboard
      }
      navigate({ to: paths.dashboard.root });
    }
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error(t('toasts.fillAllFields'));
      return;
    }

    setLoading(true);

    try {
      const { accessToken, refreshToken } = await authControllerSignin({ email, password });
      await completeLogin(accessToken, refreshToken);
    } catch (err: any) {
      console.error('Sign in error:', err);
      const message = err?.message || t('signIn.error');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = async (idToken: string) => {
    setLoading(true);
    try {
      const { accessToken, refreshToken } = await authControllerGoogleSignin({ idToken });
      await completeLogin(accessToken, refreshToken);
    } catch (err: any) {
      console.error('Google sign in error:', err);
      toast.error(err?.message || t('signIn.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, letterSpacing: '-0.02em' }}>
        {t('signIn.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {shopifyState
          ? alreadyConnected
            ? t('signIn.subtitleShopifyConnected')
            : t('signIn.subtitleShopify')
          : t('signIn.subtitle')}
      </Typography>

      <Stack component="form" spacing={2.5} onSubmit={handleEmailLogin}>
        <TextField
          fullWidth
          label={t('fields.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          autoComplete="email"
        />

        <TextField
          fullWidth
          label={t('fields.password')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          autoComplete="current-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ textAlign: 'right', mt: -0.5 }}>
          <Link component={RouterLink} href={paths.auth.forgotPassword} sx={{ cursor: 'pointer' }}>
            <Typography variant="body2">{t('signIn.forgotPassword')}</Typography>
          </Link>
        </Box>

        <Button fullWidth size="large" type="submit" variant="contained" disabled={loading} sx={{ py: 1.4 }}>
          {loading ? t('signIn.submitting') : t('signIn.submit')}
        </Button>

        <Divider sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          {t('signIn.or', 'or')}
        </Divider>

        <GoogleSignInButton onCredential={handleGoogleCredential} text="continue_with" />
      </Stack>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('signIn.noAccount')}{' '}
          <Link
            component={RouterLink}
            href={
              shopifyState
                ? `${paths.auth.signUp}?shopify_state=${shopifyState}`
                : redirectTo
                  ? `${paths.auth.signUp}?redirect=${encodeURIComponent(redirectTo)}`
                  : paths.auth.signUp
            }
            sx={{ cursor: 'pointer', fontWeight: 600 }}
          >
            {t('links.signUp')}
          </Link>
        </Typography>
      </Box>
    </>
  );
}
